import {createStyles} from '@mantine/core';
import React, {ReactNode} from 'react';

import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {UseSelectedAnalysisItemOutput} from 'analysis-tool/ui/hooks/useSelectedAnalysisItem';
import {StepInitializationAnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {SidebarItemData} from './SidebarItemDatas';
import {SidebarSectionItems} from './SidebarSection';
import ToggleAnalysisSidebarButton from './ToggleAnalysisSidebarButton';

const useStyles = createStyles((theme) => ({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 8,
    paddingRight: 8,
  },
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    textTransform: 'uppercase',
    color: theme.colors.gray[8],
    letterSpacing: 1,
  },
  collapseToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 15,
    marginRight: 4,
    // Reset button styles so the toggle looks like the original caret icon
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  collapseToggleIcon: {
    color: theme.colors.gray[6],
    fontSize: 12,
  },
  toggleSidebarButton: {
    marginLeft: 'auto',
  },
}));

/** @package */
export interface CollapsibleSidebarSectionProps {
  analysis: PipelineGraphQL;
  headerButton?: ReactNode;
  onToggleSidebar?: () => void;
  title: string;
  sidebarItemDatas: Array<SidebarItemData>;
  selectedItemState: UseSelectedAnalysisItemOutput;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onOpenStepInitializationView: (newSelectedItem: StepInitializationAnalysisMainViewItem) => void;
  defaultExpandedTier?: number;
  expandedStates: Record<string, boolean>;
  onToggleExpand: (sectionKey: string, isCurrentlyExpanded: boolean) => void;
}

/** @package */
export default function CollapsibleSidebarSection({
  analysis,
  headerButton,
  onToggleSidebar,
  title,
  sidebarItemDatas,
  selectedItemState,
  onChangeSelectedRightSidebarItem,
  onOpenStepInitializationView,
  defaultExpandedTier,
  expandedStates,
  onToggleExpand,
}: CollapsibleSidebarSectionProps): JSX.Element {
  const {classes} = useStyles();

  const sectionKey = title;
  const isExpanded = expandedStates[sectionKey] !== false;

  return (
    <div
      role="region"
      aria-label={title}
      {...getTestProps({component: 'CollapsibleSidebarSection', element: 'root', key: title})}
    >
      <div
        className={classes.sectionHeader}
        {...getTestProps({component: 'CollapsibleSidebarSection', element: 'header'})}
      >
        <button
          type="button"
          className={classes.collapseToggle}
          onClick={() => {
            onToggleExpand(sectionKey, isExpanded);
          }}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
        >
          <BenchlingIcon
            icon={isExpanded ? 'caret-down' : 'caret-right'}
            className={classes.collapseToggleIcon}
          />
        </button>
        <div className={classes.sectionTitle}>{title}</div>
        {headerButton}
        {onToggleSidebar && (
          <div className={classes.toggleSidebarButton}>
            <ToggleAnalysisSidebarButton sidebarIsOpen={true} toggleSidebar={onToggleSidebar} />
          </div>
        )}
      </div>
      {isExpanded && (
        <SidebarSectionItems
          analysis={analysis}
          sidebarItemDatas={sidebarItemDatas}
          selectedItemState={selectedItemState}
          onOpenStepInitializationView={onOpenStepInitializationView}
          onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
          defaultExpandedTier={defaultExpandedTier}
          indentMode="padding"
          expandedStates={expandedStates}
          onToggleExpand={onToggleExpand}
        />
      )}
    </div>
  );
}
